import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WarningCircle, CircleNotch, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const ADMIN_STATUSES = [
  "Draft",
  "Sent",
  "Approved",
  "Payment Authorization Received",
  "Deposit Confirmed",
  "Paid in Full",
  "Booked",
];

export function StatusCorrectionDialog({ itinerary, open, onOpenChange, onSuccess }) {
  const currentStatus = itinerary?.status || "Draft";
  const [newStatus, setNewStatus] = useState(currentStatus);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmingFromBooked, setConfirmingFromBooked] = useState(false);

  // Reset whenever opening for a different itinerary
  React.useEffect(() => {
    if (open) {
      setNewStatus(itinerary?.status || "Draft");
      setReason("");
      setConfirmingFromBooked(false);
      setSaving(false);
    }
  }, [open, itinerary?.id]); // eslint-disable-line

  if (!itinerary) return null;

  const leavingBooked = currentStatus === "Booked" && newStatus !== "Booked";
  const canSubmit =
    newStatus !== currentStatus &&
    (!leavingBooked || reason.trim().length >= 4) &&
    !saving;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/itineraries/${itinerary.id}/correct-status`,
        { new_status: newStatus, reason: reason.trim() }
      );
      toast.success(
        `Status updated: ${data.old_status} → ${data.new_status}` +
          (data.commissions_reversed ? " · commissions reversed" : "")
      );
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update status");
    } finally {
      setSaving(false);
      setConfirmingFromBooked(false);
    }
  };

  const handleSaveClick = () => {
    if (leavingBooked) {
      // Open the additional confirmation alert before posting
      setConfirmingFromBooked(true);
    } else {
      submit();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="bg-ink-surface border-white/10 text-white max-w-lg"
          data-testid="status-correction-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl flex items-center gap-2">
              <ArrowsClockwise size={24} weight="duotone" className="text-[#D4AF37]" />
              Correct trip status
            </DialogTitle>
            <DialogDescription className="text-white/55 text-xs font-sans">
              {itinerary.title} · {itinerary.client_name || "—"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div className="rounded-md border border-white/10 bg-black/30 p-3">
              <p className="text-[10px] uppercase tracking-widest text-white/45 font-sans mb-1">Current status</p>
              <p
                className="text-white font-serif text-lg"
                data-testid="status-correction-current"
              >
                {currentStatus}
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">New status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger
                  className="bg-black/40 border-white/10 h-11"
                  data-testid="status-correction-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                  {ADMIN_STATUSES.map((s) => (
                    <SelectItem key={s} value={s} disabled={s === currentStatus}>
                      {s}
                      {s === currentStatus ? "  (current)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {leavingBooked && (
              <div className="flex items-start gap-2 p-3 rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-200">
                <WarningCircle size={18} weight="duotone" className="mt-0.5 shrink-0" />
                <div className="text-xs font-sans">
                  This may affect <strong>commissions</strong>, <strong>leaderboard sales</strong>, and{" "}
                  <strong>client status</strong>. Linked commissions will be reversed (already-paid rows are
                  preserved). Continue with caution.
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                Reason {leavingBooked && <span className="text-amber-300 normal-case tracking-normal">(required)</span>}
              </Label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Agent accidentally marked as booked."
                className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                data-testid="status-correction-reason"
              />
              <p className="text-[10px] text-white/35 font-sans mt-1">
                Logged with admin name + timestamp on the trip's audit trail. The owning agent will be emailed.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11"
              data-testid="status-correction-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveClick}
              disabled={!canSubmit}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 px-6 text-xs uppercase tracking-widest"
              data-testid="status-correction-save"
            >
              {saving ? <CircleNotch size={16} className="animate-spin" /> : "Save change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extra confirmation when leaving Booked */}
      <AlertDialog open={confirmingFromBooked} onOpenChange={(o) => !o && setConfirmingFromBooked(false)}>
        <AlertDialogContent className="bg-ink-surface border-amber-400/30 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl flex items-center gap-2">
              <WarningCircle size={22} weight="duotone" className="text-amber-300" />
              Confirm status change
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/65 font-sans">
              This may affect commissions, leaderboard sales, and client status. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/20 text-white hover:bg-white/5"
              data-testid="status-correction-confirm-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={submit}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="status-correction-confirm-yes"
            >
              Yes, continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
